import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { LogOut, Shield, LayoutGrid, Briefcase, BookOpen, FileText, Home, Building2 } from "lucide-react";

const studentLinks = [
  { to: "/dashboard", tKey: "nav.dashboard", icon: Home },
  { to: "/courses", tKey: "nav.courses", icon: LayoutGrid },
  { to: "/jobs", tKey: "nav.jobs", icon: Briefcase },
  { to: "/my-learning", tKey: "nav.myLearning", icon: BookOpen },
  { to: "/my-applications", tKey: "nav.myApplications", icon: FileText },
];

const employerLinks = [
  { to: "/employer", tKey: "nav.dashboard", icon: Building2 },
];

export default function Navbar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = user?.role === "employer" ? employerLinks : studentLinks;
  const homeLink = user?.role === "employer" ? "/employer" : "/dashboard";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header
      className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-md"
      data-testid="main-navbar"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-8">
          <Link to={homeLink} className="flex items-center gap-2" data-testid="navbar-brand">
            <img src="/logo.png" alt="Classroom to Corporate" className="h-10 w-10 object-contain" />
            <span className="font-heading text-lg font-extrabold text-slate-900 hidden sm:block">
              Classroom to <span className="text-amber-600">Corporate</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((l) => {
              const active = location.pathname === l.to;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.to.replace(/\//g, "")}`}
                  className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                    active ? "bg-amber-50 text-amber-700" : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                  }`}
                >
                  {t(l.tKey)}
                </Link>
              );
            })}
            {user?.role === "admin" && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                  location.pathname === "/admin"
                    ? "bg-indigo-50 text-indigo-700"
                    : "text-indigo-600 hover:bg-indigo-50"
                }`}
              >
                <Shield className="h-4 w-4" /> {t("nav.admin")}
              </Link>
            )}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2 rounded-full border border-slate-200 py-1 pl-1 pr-3 transition-colors hover:bg-slate-50"
              data-testid="profile-menu-trigger"
            >
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.picture} alt="" />
                <AvatarFallback className="bg-amber-600 text-white text-xs">
                  {user?.name?.[0]?.toUpperCase() || "?"}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium text-slate-700 hidden sm:block">{user?.name}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60" data-testid="profile-menu-dropdown">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="font-semibold text-slate-900" data-testid="menu-user-name">{user?.name}</span>
                <span className="text-xs font-normal text-slate-500 break-all" data-testid="menu-user-email">
                  {user?.email}
                </span>
                <Badge
                  data-testid="menu-user-role"
                  className={`mt-2 w-fit capitalize ${user?.role === "admin" ? "bg-indigo-600" : user?.role === "employer" ? "bg-indigo-900" : "bg-amber-600"}`}
                >
                  {user?.role}
                </Badge>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {user?.role === "admin" && (
              <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin-link">
                <Shield className="mr-2 h-4 w-4" /> {t("nav.adminPortal")}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={handleLogout} data-testid="logout-button" className="text-red-600">
              <LogOut className="mr-2 h-4 w-4" /> {t("nav.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </div>

      {/* Mobile nav */}
      <nav className="md:hidden flex items-center gap-1 overflow-x-auto border-t border-slate-100 px-4 py-2">
        {navLinks.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-semibold ${
              location.pathname === l.to ? "bg-amber-50 text-amber-700" : "text-slate-500"
            }`}
          >
            {t(l.tKey)}
          </Link>
        ))}
        {user?.role === "admin" && (
          <Link to="/admin" className="whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-semibold text-indigo-600">
            {t("nav.admin")}
          </Link>
        )}
      </nav>
    </header>
  );
}
